/**
 * LiveMonitor.tsx — T318 / FR-007 + FR-016 + FR-021 wired surface.
 *
 * Replaces the prototype's mock-store read with the reactive composite
 * hook useLiveMonitorTiles (T308), bound to three fleet-scale Convex
 * subscriptions (channels.listChannels, channelState
 * .getRunningChannelsSnapshot, validationLogs.getRecentForRunningChannels).
 * The hook merges them into one tile row per running channel and
 * surfaces a not-wired flag whenever the per-tile logs query is still
 * unresolved (the v.1-D silent fallback that the useLiveMonitorTiles
 * JSDoc names by name as the v.1-era defect).
 *
 * Freshness banners (FR-007) bind via the canonical wired shape — typed
 * against the eleven-key closed union exported by useFreshness. Two
 * rollup keys cover this screen: bridge-health-sweep (10-min sweep that
 * powers the running-channels aggregate the tile grid reads) and
 * qoeBeaconAggregator (10-min aggregator for the recent-logs
 * freshness — the same query the hook subscribes to).
 *
 * FR-021 invariants the wired body upholds (pinned by the observability
 * contract tests under tests/observability):
 *
 *   - Imports useLiveMonitorTiles (a hook that uses useQuery from
 *     convex react internally) — never reads from the prototype mock
 *     store on this surface.
 *   - No pseudo-random metrics anywhere — sparklines on this surface
 *     (if any) are sourced from real reactive reads.
 *   - No manual-refresh affordance on the live tile grid: the only
 *     refresh control this codebase exposes is the one the
 *     stale-banner primitive renders when an onRefresh callback is
 *     wired, and the wired surface does NOT pass one here — letting
 *     the data refresh itself reactively, which is the contract.
 *
 * Honest-empty handling: a channel in the directory but absent from
 * the running snapshot defaults to backendStatus idle (per
 * useChannelList). A tile whose recent-logs query has not resolved
 * shows the operator-honesty glyph; a tile with a real empty log
 * window renders the empty-list path. No fabricated zeros.
 */
import { useLiveMonitorTiles } from '../../hooks/useLiveMonitorTiles';
import { navigate } from '../../lib/nav';
import {
  EmptyState,
  FreshnessBanner,
  PageHead,
  Panel,
  Pill,
  Skeleton,
  StatCard,
  Thumb,
} from '../../components/primitives';
import { adaptChannelStatus } from '../../lib/adapters';

// ---------------------------------------------------------------------------
// Thin badge tone helper — backend channelStatus literal → primitive tone.
// Mirrors the prototype's bucket but keeps the Spanish operator label
// rendering via `adaptChannelStatus` so the surface stays glossary-clean.
// ---------------------------------------------------------------------------

function statusTone(
  s: 'idle' | 'starting' | 'running' | 'ad_break' | 'stopping' | 'error',
): 'live' | 'good' | 'warn' | 'bad' | 'neutral' {
  switch (s) {
    case 'running':  return 'live';
    case 'ad_break': return 'warn';
    case 'starting': return 'warn';
    case 'stopping': return 'warn';
    case 'error':    return 'bad';
    case 'idle':     return 'neutral';
    default:         return 'neutral';
  }
}

export function LiveMonitor() {
  const tiles = useLiveMonitorTiles();

  // ──────────────────────────────────────────────────────────────────
  // Loading shell — the composite hook returns `undefined` until BOTH
  // backing subscriptions resolve. Show the skeleton row in that case.
  // ──────────────────────────────────────────────────────────────────
  if (tiles === undefined) {
    return (
      <div>
        <PageHead
          title="Monitor en vivo"
          description="Cada canal al aire, en este momento."
        />
        <FreshnessBanner cronKey="bridge-health-sweep" />
        <FreshnessBanner cronKey="qoeBeaconAggregator" />
        <div className="circa-grid-4" style={{ marginBottom: 24 }}>
          <Skeleton h={92} />
          <Skeleton h={92} />
          <Skeleton h={92} />
          <Skeleton h={92} />
        </div>
        <div className="circa-grid-3">
          <Skeleton h={260} />
          <Skeleton h={260} />
          <Skeleton h={260} />
        </div>
      </div>
    );
  }

  const counts = {
    live:     tiles.filter((t) => t.backendStatus === 'running').length,
    adBreak:  tiles.filter((t) => t.backendStatus === 'ad_break').length,
    notWired: tiles.filter((t) => t.notWired).length,
    total:    tiles.length,
  };

  return (
    <div>
      <PageHead
        title="Monitor en vivo"
        description="Cada canal al aire, en este momento."
      />
      <FreshnessBanner cronKey="bridge-health-sweep" />
      <FreshnessBanner cronKey="qoeBeaconAggregator" />

      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label="Al aire"
          value={counts.live}
          tone={counts.live > 0 ? 'good' : undefined}
          icon="radio"
        />
        <StatCard
          label="En pausa publicitaria"
          value={counts.adBreak}
          tone={counts.adBreak > 0 ? 'warn' : undefined}
          icon="pause"
        />
        <StatCard
          label="Sin datos en vivo"
          value={counts.notWired}
          tone={counts.notWired > 0 ? 'warn' : undefined}
          icon="alertTriangle"
          hint="señal sin reportar"
        />
        <StatCard label="Total en grilla" value={counts.total} icon="tv" />
      </div>

      {tiles.length === 0 ? (
        <Panel pad={0}>
          <div style={{ padding: 24 }}>
            <EmptyState
              icon="radio"
              title="Sin canales al aire"
              body="No hay transmisiones en curso en este momento."
            />
          </div>
        </Panel>
      ) : (
        <div className="circa-grid-3">
          {tiles.map((t) => {
            const tone = statusTone(t.backendStatus);
            const statusLabel = adaptChannelStatus(t.backendStatus);
            const recentLogCount = t.logs.length;
            return (
              <div
                key={t._id}
                onClick={() => navigate(`/monitoring/${t._id}`)}
                className="circa-lift"
                style={{
                  border: '1px solid var(--border-cool)',
                  borderRadius: 22,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'var(--canvas)',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Thumb radius={0} />
                  <span style={{ position: 'absolute', top: 12, right: 12 }}>
                    <Pill tone={tone}>{statusLabel}</Pill>
                  </span>
                  {t.notWired && (
                    <span style={{ position: 'absolute', top: 12, left: 12 }}>
                      <Pill tone="warn" size="sm">
                        Sin datos en vivo
                      </Pill>
                    </span>
                  )}
                </div>
                <div style={{ padding: 16 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 20,
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                  >
                    {t.name}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--body-muted)',
                      marginBottom: 12,
                    }}
                  >
                    {t.notWired
                      ? '—'
                      : recentLogCount === 0
                        ? 'Sin eventos recientes'
                        : `${recentLogCount} evento${recentLogCount === 1 ? '' : 's'} reciente${recentLogCount === 1 ? '' : 's'}`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LiveMonitor;
